/*
 * Copyright (C) 2024 S&P Global.
 * All Rights Reserved
 */
import { AppIdentifier, DesktopAgent } from "@finos/fdc3";
import { agentProviderNamePfdc3, getFormattedTime, CustomEventType } from "fusion-interop-common/src";
import { InteropService } from "fusion-interop-common/src/messaging";
import { AppIdentifierService, AppRefManager } from "fusion-interop-common/src/lifecycle";
import { AgentFactory, AppType } from "fusion-interop-common/src/types";
import { AppDefinition } from "fusion-interop-common/src/types/app-directory";
import { FusionInteropMessage, MessageType, SystemMessageType, TargetableWindow } from "fusion-interop-common/src/types/message";
import { DiscoverPeerRequestPayload, RegisterWithPeerRequestPayload, UnregisterWithPeerRequestPayload } from "fusion-interop-common/src/types/message/payloads";
import { HandshakeRequestPayload, HandshakeResponsePayload } from "fusion-interop-common/src/types/message/payloads/common";
import { RoutePattern } from "fusion-interop-common/src/types/routing";
import { AppRegistrationListener } from "fusion-interop-common/src/types/lifecycle";

export class Pfdc3 {
  private _browserGlobalScope: Window & typeof globalThis;
  private _interopService: InteropService;
  private _appIdentifierService: AppIdentifierService;
  private _appRefManager: AppRefManager;
  private _pfdc3AgentFactory: AgentFactory;
  private _appDef: AppDefinition | undefined;

  // prettier-ignore
  constructor(browserGlobalScope: Window & typeof globalThis,
              interopService: InteropService,
              appIdentifierService: AppIdentifierService,
              appRefManager: AppRefManager,
              pfdc3AgentFactory: AgentFactory) {
    console.log("Pfdc3.constructor");
    if (!browserGlobalScope) {
      throw new Error("browserGlobalScope not provided");
    }
    if (!interopService) {
      throw new Error("interopService not provided");
    }
    if (!appIdentifierService) {
      throw new Error("appIdentifierService not provided");
    }
    if (!appRefManager) {
      throw new Error("appRefManager not provided");
    }
    if (!pfdc3AgentFactory) {
      throw new Error("pfdc3AgentFactory not provided");
    }
    this._browserGlobalScope = browserGlobalScope;
    this._interopService = interopService;
    this._appIdentifierService = appIdentifierService;
    this._appRefManager = appRefManager;
    this._pfdc3AgentFactory = pfdc3AgentFactory;
    // If current app is a parent app, create system message route handlers for discoverPeerRequest, registerWithPeerRequest and unregisterWithPeerRequest
    if (this._interopService.sourceAppType === AppType.parent) {
      this._createDiscoverPeerRequestRouteHandler();
      this._createRegisterWithPeerRequestRouteHandler();
      this._createUnregisterWithPeerRequestRouteHandler();
    }
  }

  public async installAgent(appId: string, appDef: AppDefinition, appRegistrationListener: AppRegistrationListener): Promise<void> {
    //TODO - Why does this accept an appId argument if the appId is contained within the appDef object?
    console.log("Pfdc3.installAgent");
    if (!appId) {
      throw new Error("appId not provided");
    }
    if (!appDef || !appDef.appId || !appDef.acceptPeerOrigins || appDef.acceptPeerOrigins.length < 1) {
      throw new Error("appDef not provided or incomplete");
    }
    this._appDef = appDef;
    this._appRefManager.subscribe(appRegistrationListener);

    if (this._browserGlobalScope.fdc3) {
      console.log(`\n**\n**\n** FDC3 is already supported in this environment. Therefore ${agentProviderNamePfdc3} will not be installed. **\n**\n**`);
    } else {
      const appIdentifier: AppIdentifier = this._appIdentifierService.getAppIdentifier(appId);

      // Initialize InteropService and register current app with AppRefManager
      this._interopService.initialize(appIdentifier);
      const registerSucceeded = this._appRefManager.register(this._interopService.sourceAppType, appIdentifier, this._browserGlobalScope.location.origin, this._browserGlobalScope);
      if (!registerSucceeded) {
        throw new Error(`${this._interopService.sourceAppType} app registration failed.`);
      }

      // (window as any).interopService = this._interopService;
    
      // Create fdc3 object, but don't expose it until bootstrap is completed (e.g. after it has registered its route handlers)
      const fdc3: DesktopAgent = this._pfdc3AgentFactory(this._browserGlobalScope, appIdentifier, this._interopService, this._appRefManager, appDef);

      // Freeze the fdc3 object to prevent extensions and make its properties non-writable and non-configurable.
      // This is shallow freeze, and so it only applies to the immediate properties of the fdc3 object itself.
      // However, since all the immediate properties are methods (rather than objects) then they will now be frozen.
      // The reason for doing this is to prevent the fdc3 object's methods from being deleted or reassigned by external code at runtime.
      Object.freeze(fdc3);

      // Note that the @finos/fdc3 fdc3Ready async wrapper function has been implemented in such a way that it resolves immediately if window.fdc3 is truthy,
      // even if no 'fdc3Ready' event has yet been raised via window.dispatchEvent.  This means it is *vital* we ensure that the bootstrapping process is
      // complete (below) prior to setting window.fdc3 (further below).
      // Failure to consider this fact will result in a "`fdc3Ready` event fired, but `window.fdc3` not set to DesktopAgent." error at runtime.

      //TODO - Bootstrap FDC3 Agent

      // Set the window.fdc3 property, but do so using Object.defineProperty rather than using a simple 'window.fdc3 = ...' assignment.
      // The reason for doing it this way is to prevent window.fdc3 being deleted or reassigned by external code at runtime.
      // Clearly we cannot (and would not want to) freeze the window object itself, but we can at least ensure that the window.fdc3 property is non-configurable / non-writable.
      // Note that setting 'const fdc3 = ...' in the global namespace would achieve a similar result, but in practice it would not be not straightforward to
      // do that since script bundling processes typically wrap all code using the revealing module pattern to avoid polluting the global namespace.
      // The revealing module pattern effectively ensures that anything declared in a library's code by means of var/let/const is private to that library by default,
      // and can only be made available externally by explicit export statements or by assignment as new properties / extensions on an existing object in the global namespace.
      Object.defineProperty(this._browserGlobalScope, "fdc3", {
        value: fdc3,
        configurable: false,
        writable: false,
      });
      console.log(`\n**\n**\n** ${agentProviderNamePfdc3} has been installed in ${this._browserGlobalScope.location.origin} at ${getFormattedTime()} **\n**\n**`);

      // Raise the 'fdc3Ready' event to notify the consuming app that the agent is now ready for use.
      // The consuming app should wait for this event before it attempts invocation of any method on the window.fdc3 object.
      this._browserGlobalScope.dispatchEvent(new Event(CustomEventType.fdc3Ready));
    }
  }

  private _createDiscoverPeerRequestRouteHandler(): void {
    const routePattern: RoutePattern = {
      type: MessageType.spgiFusionSystem,
      systemMessageType: SystemMessageType.discoverPeerRequest,
    };
    this._interopService.addRouteHandler(routePattern, (requestMessage: FusionInteropMessage<DiscoverPeerRequestPayload>, requestOrigin: string, requestSourceWindow: WindowProxy): void => {
      if (!this._interopService.isInitialized) {
        throw new Error("InteropService not initialized");
      }
      const responsePayload = this._validateHandshakeRequestAndCreateResponse(requestMessage, requestOrigin, undefined, SystemMessageType.discoverPeerResponse);
      const targetableWindow: TargetableWindow = {
        origin: requestOrigin,
        window: requestSourceWindow,
      };
      this._interopService.respond(responsePayload, requestMessage, targetableWindow);
    });
  }

  private _createRegisterWithPeerRequestRouteHandler(): void {
    const routePattern: RoutePattern = {
      type: MessageType.spgiFusionSystem,
      systemMessageType: SystemMessageType.registerWithPeerRequest,
    };
    this._interopService.addRouteHandler(routePattern, (requestMessage: FusionInteropMessage<RegisterWithPeerRequestPayload>, requestOrigin: string, requestSourceWindow: WindowProxy): void => {
      if (!this._interopService.isInitialized) {
        throw new Error("InteropService not initialized");
      }
      // Register child app with AppRefManager
      //TODO - registerWithPeerRequest processing - need to validate child app origin BEFORE registering child app with AppRefManager
      //TODO - Also validate that registerWithPeerRequest window and origin are the same as discoverPeerRequest window and origin
      let errorReason: string | undefined;
      const registerSucceeded = this._appRefManager.register(AppType.child, requestMessage.payload.appIdentifier, requestOrigin, requestSourceWindow);
      if (!registerSucceeded) {
        errorReason = "Child app registration failed.";
      }
      const responsePayload = this._validateHandshakeRequestAndCreateResponse(requestMessage, requestOrigin, errorReason, SystemMessageType.registerWithPeerResponse);
      const targetableWindow: TargetableWindow = {
        origin: requestOrigin,
        window: requestSourceWindow,
      };
      this._interopService.respond(responsePayload, requestMessage, targetableWindow);
    });
  }

  private _createUnregisterWithPeerRequestRouteHandler(): void {
    const routePattern: RoutePattern = {
      type: MessageType.spgiFusionSystem,
      systemMessageType: SystemMessageType.unregisterWithPeerRequest,
    };
    this._interopService.addRouteHandler(routePattern, (requestMessage: FusionInteropMessage<UnregisterWithPeerRequestPayload>, requestOrigin: string, requestSourceWindow: WindowProxy): void => {
      if (!this._interopService.isInitialized) {
        throw new Error("InteropService not initialized");
      }
      // Unregister child app with AppRefManager
      //TODO - unregisterWithPeerRequest processing - need to validate child app origin BEFORE unregistering child app with AppRefManager
      //TODO - Also validate that unregisterWithPeerRequest window and origin are the same as registerWithPeerRequest window and origin
      let errorReason: string | undefined;
      const unregisterSucceeded = this._appRefManager.unregister(requestMessage.payload.appIdentifier, requestOrigin);
      if (!unregisterSucceeded) {
        errorReason = "Child app unregistration failed.";
      }
      const responsePayload = this._validateHandshakeRequestAndCreateResponse(requestMessage, requestOrigin, errorReason, SystemMessageType.registerWithPeerResponse);
      const targetableWindow: TargetableWindow = {
        origin: requestOrigin,
        window: requestSourceWindow,
      };
      this._interopService.respond(responsePayload, requestMessage, targetableWindow);
    });
  }

  private _validateHandshakeRequestAndCreateResponse(requestMessage: FusionInteropMessage<HandshakeRequestPayload>, requestOrigin: string, errorReason: string | undefined, responseSystemMessageType: SystemMessageType): HandshakeResponsePayload {
    //TODO - Validate agent has been installed successfully (this is required because we need to rely on this._appDef)
    //TODO - Validate spgiFusionInteropVersion from requestMessage against current (parent app) spgiFusionInteropVersion
    //TODO - Validate non-empty requestMessage.payload.data.appIdentifier
    let result = false;
    if (errorReason) {
      console.error(errorReason);
    }
    else if (!this._appDef?.acceptPeerOrigins?.includes(requestOrigin)) {
      errorReason = `Peer origin ${requestOrigin} is rejected by app running on ${this._browserGlobalScope.location.origin}.`;
      console.error(errorReason);
    }
    else {
      result = true;
    }
    return <HandshakeResponsePayload>{
      type: responseSystemMessageType,
      appIdentifier: this._interopService.sourceAppIdentifier,
      result,
      errorReason,
    };
  }
}
